import { isAndroidApp } from "@/utils/pwaContext";

/**
 * Central GA4 event helper for the purchase funnel (Itération 49 - diagnostic abandon panier).
 * Every event automatically carries a `platform` param ("web" | "android") so the funnel can
 * be segmented by launch context directly in GA4 Explore, without every call site having to
 * remember to pass it. No-ops silently if gtag isn't loaded (ad blocker, etc.) - must never
 * throw or block the purchase flow.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  try {
    // Couche centrale de mesure (18/08/2026) : chaque événement porte des IDs de corrélation
    // (event_id/session_id/user_id) + le contexte d'acquisition (UTM premier contact) + la
    // version produit -> réconciliation GA4 <-> Google Ads <-> Meta <-> base de données.
    const enriched = {
      platform: isAndroidApp() ? "android" : "web",
      device_category: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
      app_version: process.env.REACT_APP_VERSION || "1.0",
      event_id: `ev_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
      session_id: getSessionId(),
      user_type: localStorage.getItem("ams_token") ? "registered" : "guest",
      ...getAcquisition(),
      ...params,
    };
    if (typeof window.gtag === "function") {
      window.gtag("event", name, enriched);
    }
    // Miroir vers le pixel Meta (installé 18/08/2026) : événements standards du funnel pour
    // que Meta Ads optimise sur les conversions. No-op si fbq absent (adblock).
    if (typeof window.fbq === "function") {
      const FB_MAP = {
        begin_checkout: "InitiateCheckout",
        purchase: "Purchase",
        sign_up: "CompleteRegistration",
        mollie_checkout_redirect: "AddPaymentInfo",
        view_pricing: "ViewContent",
        checkout_started: "InitiateCheckout",
      };
      const fbEvent = FB_MAP[name];
      if (fbEvent) {
        window.fbq("track", fbEvent, {
          currency: params.currency || "EUR",
          ...(params.value != null ? { value: params.value } : {}),
        }, { eventID: enriched.event_id });
      }
    }
  } catch {
    // Never let analytics break the app.
  }
}

function getSessionId() {
  let sid = sessionStorage.getItem("ams_sid");
  if (!sid) {
    sid = `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("ams_sid", sid);
  }
  return sid;
}

// Capture UNE FOIS par session les UTM/click-ids d'arrivée (persistés aussi en premier contact
// localStorage pour attribuer les achats à la campagne d'origine).
function getAcquisition() {
  try {
    let acq = sessionStorage.getItem("ams_acq");
    if (!acq) {
      const q = new URLSearchParams(window.location.search);
      const data = {};
      for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
        if (q.get(k)) data[k] = q.get(k);
      }
      if (q.get("fbclid")) data.utm_source = data.utm_source || "facebook_ads";
      if (q.get("ttclid")) data.utm_source = data.utm_source || "tiktok_ads";
      if (!data.utm_source && document.referrer) {
        try { data.ref_domain = new URL(document.referrer).hostname; } catch { /* noop */ }
      }
      acq = JSON.stringify(data);
      sessionStorage.setItem("ams_acq", acq);
      if (!localStorage.getItem("ams_first_touch") && Object.keys(data).length) {
        localStorage.setItem("ams_first_touch", acq);
      }
      if (Object.keys(data).length || true) {
        // landing_view : 1 seule fois par session, avec la page d'atterrissage exacte
        setTimeout(() => {
          if (typeof window.gtag === "function") {
            window.gtag("event", "landing_view", {
              landing_page: window.location.pathname,
              session_id: getSessionId(),
              platform: isAndroidApp() ? "android" : "web",
              ...data,
            });
          }
        }, 0);
      }
    }
    const first = localStorage.getItem("ams_first_touch");
    return { ...(first ? { acquisition_channel: (JSON.parse(first).utm_source || JSON.parse(first).ref_domain || "direct") } : {}), ...JSON.parse(acq) };
  } catch {
    return {};
  }
}
